// recomputeTicketPredictedMinutes — Phase 4 canonical helper.
//
// Single shared entry point for: sum ticket_jobs minutes → update tickets.predicted_minutes
// → trigger board cascade (and rollover-tail propagation when applicable).
//
// AUTHORITY CONTRACT:
// - ticket_jobs is the SOLE time-bearing scheduler authority.
// - Every mutation that affects job time (Add Job, Add Labor standalone, Add Labor
//   attached, Edit job minutes, Delete job) MUST funnel through this helper or
//   inline the equivalent logic — NEVER bypass it.
//
// This mirrors the inline path in AddJobDialog.executeJobAdd to keep behavior
// identical across all entry points.

import { supabase } from '@/integrations/supabase/client';
// Static (FLOYD 2026-10-26): a lazily loaded chunk is removed by the next deploy from under an open tab.
import { syncTicketBreakRowsWithSchedule } from '@/utils/syncTicketBreakRows';
import { getShopDayLabel, getTodayInShopTz } from '@/utils/shopTime';
import { mutateTicketSchedule } from '@/utils/mutateTicketSchedule';
// DTC-F: route the `last_edited_at` audit-trail write through the
// canonical clock so audit timestamps are deterministic under
// `clock.setFixed(...)`. Same drift class DTC-D fixed in
// `allocate-ticket-time`'s `last_completed_at` writes.
import { clock } from '@/utils/clock';

export interface RecomputeResult {
  totalPredictedMinutes: number;
  hasTeardownJobs: boolean;
}

/**
 * Recompute tickets.predicted_minutes from the sum of all ticket_jobs and
 * trigger the canonical cascade chain. Returns the new total.
 *
 * Safe to call from any UI entry point that mutates ticket_jobs.
 */
export async function recomputeTicketPredictedMinutes(
  ticketId: string,
  options?: { userId?: string; skipCascade?: boolean }
): Promise<RecomputeResult> {
  // Re-sum from scratch — prevents accumulation bugs from race conditions.
  const { data: allTicketJobs, error: tjErr } = await supabase
    .from('ticket_jobs')
    .select('minutes_override, teardown_enabled, jobs:job_id(standard_minutes)')
    .eq('ticket_id', ticketId);

  if (tjErr) {
    console.error('[recomputeTicketPredictedMinutes] Failed to load ticket_jobs:', tjErr);
    throw tjErr;
  }

  let totalPredictedMinutes = 0;
  for (const tj of allTicketJobs || []) {
    const jobMinutes = tj.minutes_override ?? (tj.jobs as any)?.standard_minutes ?? 0;
    totalPredictedMinutes += jobMinutes;
  }

  const hasTeardownJobs =
    allTicketJobs?.some((tj: any) => tj.teardown_enabled) || false;

  // Fetch ticket context needed for cascade + EOD checks.
  const { data: ticketData, error: tErr } = await supabase
    .from('tickets')
    .select(
      'shop_id, assigned_tech_id, start_time, customer_name, scheduled_date, parent_ticket_id, sequence_index, rollover_of_ticket_id, split_reason'
    )
    .eq('id', ticketId)
    .single();

  if (tErr || !ticketData) {
    console.error('[recomputeTicketPredictedMinutes] Failed to load ticket:', tErr);
    throw tErr ?? new Error('ticket not found');
  }

  // ROLLOVER CHAIN: a chain row's predicted_minutes is its SHARE of the
  // chain's job ledger — "every job on this ticket" is the whole chain's total
  // on a root, or a copy of it on a continuation. Writing it would double
  // count. On a chain row only the companion rollups are written, and the
  // chain authority re-derives every share from the ledger (below).
  const { isRolloverChainRow, recomputeRolloverChainWork } = await import('@/utils/rolloverChain');
  const isChainRow = await isRolloverChainRow(ticketId);

  // Update predicted_minutes (cascade reads this).
  // V-A-5: route the schedule write through the canonical helper.
  // - `predicted_minutes` is allowlisted (schedule field).
  // - `has_teardown_jobs`, `last_edited_*` are companion metadata, written
  //   atomically with the schedule field. We do NOT pass `options.userId`
  //   to the helper because the original write set `last_edited_at`
  //   unconditionally and `last_edited_by_user_id` only when defined; the
  //   helper's userId-gated auto-write would skip the timestamp on system
  //   recomputes (no userId), changing the audit trail. Companion fields
  //   replicate the original conditional write exactly.
  // - `skipCascade: true` PRESERVES the bespoke post-update logic below
  //   (break-row sync + rollover-tail propagation + EOD check + cascade)
  //   that the helper's generic auto-cascade does not perform.
  const updateResult = await mutateTicketSchedule(
    ticketId,
    isChainRow ? {} : { predicted_minutes: totalPredictedMinutes },
    {
      reason: 'recompute_ticket_predicted_minutes',
      skipCascade: true,
      companionFields: {
        has_teardown_jobs: hasTeardownJobs,
        last_edited_at: clock.now().toISOString(),
        ...(options?.userId !== undefined
          ? { last_edited_by_user_id: options.userId }
          : {}),
      },
    },
  );

  if (!updateResult.success) {
    const updErr = new Error(updateResult.error || 'recompute update failed');
    console.error('[recomputeTicketPredictedMinutes] Failed to update ticket:', updErr);
    throw updErr;
  }

  if (isChainRow) {
    // One atomic re-plan of the chain from its job ledger (it cascades the
    // lanes it touches). skipCascade does not skip it: without it the job
    // line would exist and the chain would not carry its time.
    await recomputeRolloverChainWork(ticketId);
  }

  if (options?.skipCascade) {
    return { totalPredictedMinutes, hasTeardownJobs };
  }

  // Resolve shop timezone for date derivation.
  const { data: shopTzData } = await supabase
    .from('shop_profile')
    .select('time_zone')
    .eq('shop_id', ticketData.shop_id)
    .maybeSingle();
  const resolvedTimezone = shopTzData?.time_zone || 'America/Los_Angeles';
  const dateStr =
    (ticketData as any).scheduled_date ??
    (ticketData.start_time
      ? getShopDayLabel(ticketData.start_time, resolvedTimezone)
      // Chronomancer R-PAST-15: use canonical clock-aware helper rather
      // than raw `new Date()` — matches R-PAST-4 in shopTime.ts.
      : getTodayInShopTz(resolvedTimezone));

  // Sync break rows (only if assigned).
  if (ticketData.assigned_tech_id && ticketData.start_time) {
    try {
      await syncTicketBreakRowsWithSchedule({
        techId: ticketData.assigned_tech_id,
        dateStr,
      });
    } catch (e) {
      console.error('[recomputeTicketPredictedMinutes] break-row sync failed:', e);
    }

    // EOD violation check (a chain's planned days never pass their dated close).
    if (!isChainRow) try {
      const { checkEODViolation } = await import('@/utils/eodChecker');
      await checkEODViolation(
        ticketId,
        totalPredictedMinutes,
        ticketData.start_time,
        ticketData.assigned_tech_id,
        ticketData.customer_name || 'Unknown Customer'
      );
    } catch (e) {
      console.error('[recomputeTicketPredictedMinutes] EOD check failed:', e);
    }
  }

  // Global cascade (always — covers unassigned tickets too).
  try {
    const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
    await triggerBoardCascade(
      ticketData.shop_id,
      dateStr,
      ticketData.assigned_tech_id || undefined
    );
  } catch (e) {
    console.error('[recomputeTicketPredictedMinutes] cascade failed:', e);
  }

  return { totalPredictedMinutes, hasTeardownJobs };
}
